import React from 'react';
import { useAsync, useSetState } from 'react-use';

const useHttp = ({
  endpoint = null,
  initialConfig = {},
  cb = () => {},
  deps = [],
}) => {
  if (
    typeof initialConfig !== 'object' ||
    typeof cb !== 'function' ||
    !Array.isArray(deps)
  ) {
    throw new Error('Invalid arguments!');
  }
  const [config, setConfig] = useSetState(initialConfig);
  const state = useAsync(async () => {
    if (endpoint) {
      if (typeof endpoint !== 'function') {
        throw new Error('Endpoint must be a function!');
      }
      const response = await endpoint(config);
      return response;
    }

    return null;
  }, [config, ...deps]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callBack = React.useCallback(cb, [state.value]);

  React.useEffect(() => {
    if (state.value) {
      callBack(state.value);
    }
  }, [state.value, callBack]);

  return { ...state, reFetch: setConfig };
};

export default useHttp;

// DESCRIPTION:
// hook for fetching the data and injecting the response into the state of the targeted component
// params:
// - config object:
//   - endpoint: if its value is null, the request will not be made. otherwise it should be a function returning a promise
//   - initialConfig: external config object that is passed down to axios. here we can pass any request config, such as query params etc.
//   - cb: a function that is triggered after the state is updated (data is fetched)
//   - deps: gives control to the user to decide when to run the hook (fetch the data)
// returns:
// - object:
//   - state of the response
//   - reFetch: function for reFetching the data

// USAGE:
// const data = useHttp({
//   endpoint: () => Data.get(),
//   initialConfig: {
//     pagination: false,
//     params: {
//       page: 2
//     }
//   },
//   cb: console.log
//   deps: [x, y]
// });

// NOTE: if you are calling the endpoint conditionaly (value is null), you need to pass the condition variables as deps
// const workflowStepsData = useHttp({
//   endpoint: wfId ? () => WorkflowSteps.getAll(wfId) : null,
//   deps: [wfId],
// });
